import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { productService, Product, CreateProductDTO } from '../services/productService.ts';
import { FaPlus, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import EditableContent from './EditableContent.tsx';
import SiteHeader from './layout/SiteHeader.tsx';
import SiteFooter from './layout/SiteFooter.tsx';
import ScrollProgress from './layout/ScrollProgress.tsx';
import LoadingOverlay from './layout/LoadingOverlay.tsx';
import { useContentManager } from '../hooks/useContentManager.ts';
import { useAssetsReady } from '../hooks/useAssetsReady.ts';
import { useScrollReveal } from '../hooks/useScrollReveal.ts';
import { useBodyClass } from '../hooks/useBodyClass.ts';
import '../styles/admin-modals.css';

// Only http(s) URLs without embedded credentials render as clickable store links
const storeHref = (value?: string | null): string | null => {
  if (!value) return null;
  try {
    const u = new URL(value);
    return ['https:', 'http:'].includes(u.protocol) && !u.username && !u.password ? u.href : null;
  } catch {
    return null;
  }
};

const StoreIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 3h7v7M21 3l-10 10" />
    <path d="M10 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5" />
  </svg>
);

const statusConfig = {
  active: { label: '現有產品', order: 1, domId: 'current' },
  coming_soon: { label: '即將推出', order: 2, domId: 'upcoming' },
  discontinued: { label: '已停產', order: 3, domId: 'discontinued' },
  hide: { label: '隱藏產品', order: 4, domId: 'hidden' },
};

const Products: React.FC = () => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const {
    isEditMode,
    isLoading: isContentLoading,
    dirtyFields,
    getContent,
    handleContentSave,
    handleDirtyStateChange,
    handleEditModeToggle,
  } = useContentManager();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CreateProductDTO>({
    product_name: '',
    short_desc: '',
    status: 'active',
    product_family: '',
    sku: '',
    revision: '',
    spec: '',
    buy_spec: '',
    price: '',
    buy_link: '',
    image_url: ''
  });
  const mainRef = useRef<HTMLElement>(null);

  // Loading modal stays up until DB content + product list arrive and the
  // page's images (logo + product photos) have loaded
  const showLoading = useAssetsReady(isLoading || isContentLoading, [
    getContent('company-logo'),
    ...products.map(p => p.image_url),
  ]);

  useBodyClass('catalog-page');
  useScrollReveal({ mainRef, enabled: !isEditMode });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const data = await productService.getAllProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      alert('載入產品時發生錯誤');
    } finally {
      setIsLoading(false);
    }
  };

  // Group products by status
  const groupedProducts = products.reduce((acc, product) => {
    const status = product.status || 'active';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  // Sort statuses by defined order, filter out 'hide' for non-admin users
  const sortedStatuses = Object.keys(groupedProducts)
    .filter(status => {
      if (status === 'hide') {
        return isAdmin;
      }
      return true;
    })
    .sort((a, b) => {
      const orderA = statusConfig[a as keyof typeof statusConfig]?.order || 999;
      const orderB = statusConfig[b as keyof typeof statusConfig]?.order || 999;
      return orderA - orderB;
    });

  // Port of catalog.js observeCategories: highlight the category link whose
  // group is currently in view
  useEffect(() => {
    if (isLoading) return;
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('.category-links a'));
    const groups = Array.from(document.querySelectorAll<HTMLElement>('.catalog-group'));
    if (!links.length || !groups.length) return;
    let queued = false;
    const update = () => {
      queued = false;
      let active = groups[0]?.id;
      for (const g of groups) {
        if (g.getBoundingClientRect().top < window.innerHeight * 0.4) active = g.id;
      }
      links.forEach(a => {
        const yes = a.hash === '#' + active;
        a.classList.toggle('selected', yes);
        if (yes) a.setAttribute('aria-current', 'location');
        else a.removeAttribute('aria-current');
      });
    };
    const onScroll = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
    };
  }, [isLoading, products, isAdmin]);

  const handleCardClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleCloseDetail = () => {
    setSelectedProduct(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const imageUrl = await productService.uploadImage(file);
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('上傳圖片時發生錯誤');
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.product_name.trim()) {
      alert('請輸入產品名稱');
      return;
    }

    try {
      // Explicitly construct payload without id (backend will generate it)
      // Convert empty strings to null for optional fields
      const createData: any = {
        product_name: formData.product_name,
        short_desc: formData.short_desc?.trim() || null,
        status: formData.status?.trim() || null,
        product_family: formData.product_family?.trim() || null,
        sku: formData.sku?.trim() || null,
        revision: formData.revision?.trim() || null,
        spec: formData.spec?.trim() || null,
        buy_spec: formData.buy_spec?.trim() || null,
        price: formData.price?.trim() || null,
        buy_link: formData.buy_link?.trim() || null,
        image_url: formData.image_url?.trim() || null
      };

      await productService.createProduct(createData);
      await loadProducts();
      setShowAddModal(false);
      resetForm();
      alert('產品新增成功');
    } catch (error: any) {
      console.error('Error creating product:', error);
      alert(error.message || '新增產品時發生錯誤');
    }
  };

  const handleEditProduct = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingProductId) {
      console.error('❌ Cannot update product: editingProductId is null');
      alert('錯誤：未選擇產品');
      return;
    }

    try {
      // Convert empty strings to null for optional fields
      const updateData: any = {
        product_name: formData.product_name,
        short_desc: formData.short_desc?.trim() || null,
        status: formData.status?.trim() || null,
        product_family: formData.product_family?.trim() || null,
        sku: formData.sku?.trim() || null,
        revision: formData.revision?.trim() || null,
        spec: formData.spec?.trim() || null,
        buy_spec: formData.buy_spec?.trim() || null,
        price: formData.price?.trim() || null,
        buy_link: formData.buy_link?.trim() || null,
        image_url: formData.image_url?.trim() || null
      };

      await productService.updateProduct(editingProductId, updateData);
      await loadProducts();
      setShowEditModal(false);
      setEditingProductId(null);
      setSelectedProduct(null);
      resetForm();
      alert('產品更新成功');
    } catch (error: any) {
      console.error('Error updating product:', error);
      alert(error.message || '更新產品時發生錯誤');
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('確定要刪除此產品嗎？此操作無法復原。')) {
      return;
    }

    try {
      await productService.deleteProduct(productId);
      await loadProducts();
      setSelectedProduct(null);
      alert('產品刪除成功');
    } catch (error: any) {
      console.error('Error deleting product:', error);
      alert(error.message || '刪除產品時發生錯誤');
    }
  };

  const resetForm = () => {
    setFormData({
      product_name: '',
      short_desc: '',
      status: 'active',
      product_family: '',
      sku: '',
      revision: '',
      spec: '',
      buy_spec: '',
      price: '',
      buy_link: '',
      image_url: ''
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProductId(product.id);
    setFormData({
      product_name: product.product_name,
      short_desc: product.short_desc || '',
      status: product.status || 'active',
      product_family: product.product_family || '',
      sku: product.sku || '',
      revision: product.revision || '',
      spec: product.spec || '',
      buy_spec: product.buy_spec || '',
      price: product.price || '',
      buy_link: product.buy_link || '',
      image_url: product.image_url || ''
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingProductId(null);
    setSelectedProduct(null);
    resetForm();
  };

  const editable = (
    contentId: string,
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'span' | 'li' | 'dt' | 'dd' | 'small' | 'strong' | 'em',
    className?: string,
    multiline?: boolean
  ) => (
    <EditableContent
      contentId={contentId}
      contentType="text"
      tag={tag}
      className={className}
      multiline={multiline}
      defaultValue={getContent(contentId)}
      isEditing={isEditMode}
      onSave={handleContentSave}
      onDirtyStateChange={handleDirtyStateChange}
    />
  );

  const renderRow = (product: Product) => {
    const price = product.price?.trim();
    const href = storeHref(product.buy_link);
    const showImage = product.image_url && !failedImages.has(product.id);
    return (
      <li className="catalog-row" key={product.id} onClick={() => handleCardClick(product)}>
        <figure className="catalog-photo">
          {showImage ? (
            <img
              src={product.image_url as string}
              alt={product.product_name}
              width={144}
              height={108}
              loading="lazy"
              onError={() => setFailedImages(prev => new Set(prev).add(product.id))}
            />
          ) : (
            <span className="photo-empty">圖片待提供</span>
          )}
        </figure>
        <div className="catalog-copy">
          <div className="product-name-price">
            <h3>{product.product_name}</h3>
            <span
              className={`price-badge${price ? '' : ' pending'}`}
              aria-label={`${product.product_name}：${price || '價格待提供'}`}
            >
              {price || '價格待提供'}
            </span>
          </div>
          <p>
            型號：{product.sku || '待提供'}　尺寸：{product.spec || '待提供'}
          </p>
          <p>必要規格：{product.buy_spec || '待提供'}</p>
          {product.short_desc && <p className="catalog-desc">{product.short_desc}</p>}
        </div>
        {href ? (
          <a
            className="store-link store-icon"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${product.product_name}：前往外部賣場（開啟新分頁）`}
            title="前往外部賣場（開啟新分頁）"
            onClick={e => e.stopPropagation()}
          >
            <StoreIcon />
          </a>
        ) : (
          <span
            className="store-link store-icon unavailable"
            role="link"
            aria-disabled="true"
            aria-label={`${product.product_name}：賣場連結待提供`}
            title="賣場連結待提供"
          >
            <StoreIcon />
          </span>
        )}
      </li>
    );
  };

  return (
    <div className={`App ${isEditMode ? 'edit-mode' : ''}`}>
      {showLoading && <LoadingOverlay />}
      <a className="skip-link" href="#main">跳至主要內容</a>
      <ScrollProgress />
      <SiteHeader
        variant="catalog"
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        isEditMode={isEditMode}
        dirtyFields={dirtyFields}
        getContent={getContent}
        onContentSave={handleContentSave}
        onDirtyStateChange={handleDirtyStateChange}
        onEditModeToggle={handleEditModeToggle}
        onLogout={logout}
      />
      <main id="main" className="catalog-main" ref={mainRef}>
        <div className="catalog-heading">
          {editable('catalog-eyebrow', 'p', 'eyebrow')}
          {editable('catalog-title', 'h1')}
          {isEditMode
            ? editable('catalog-disclaimer', 'p', 'catalog-disclaimer', true)
            : getContent('catalog-disclaimer') && (
                <p className="catalog-disclaimer">{getContent('catalog-disclaimer')}</p>
              )}
          {isAdmin && (
            <button className="add-product-btn" type="button" onClick={openAddModal}>
              <FaPlus /> 新增產品
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>載入中...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="no-products">
            <p>目前沒有產品</p>
          </div>
        ) : (
          <>
            <div className="category-links" aria-label="產品分類">
              {sortedStatuses.map(status => {
                const config = statusConfig[status as keyof typeof statusConfig];
                return (
                  <a key={status} href={`#${config?.domId || status}`}>
                    {config?.label || status}
                  </a>
                );
              })}
            </div>
            <div id="catalog-groups">
              {sortedStatuses.map(status => {
                const config = statusConfig[status as keyof typeof statusConfig];
                return (
                  <section key={status} id={config?.domId || status} className="catalog-group">
                    <div className="catalog-group-heading">
                      <h2>{config?.label || status}</h2>
                    </div>
                    <ul className="catalog-list">{groupedProducts[status].map(renderRow)}</ul>
                  </section>
                );
              })}
            </div>
          </>
        )}

        <div className="catalog-bottom">
          {editable('catalog-bottom-text', 'p')}
          {isEditMode ? (
            editable('catalog-bottom-cta-text', 'span')
          ) : (
            <a className="button light" href="/#contact">
              {getContent('catalog-bottom-cta-text')} <span>↗</span>
            </a>
          )}
        </div>
      </main>
      <div className="catalog-footer">
        <SiteFooter variant="catalog" getContent={getContent} />
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && !showEditModal && (
        <div className="modal-overlay" onClick={handleCloseDetail}>
          <div className="modal-content product-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={handleCloseDetail}>
              <FaTimes />
            </button>

            <div className="product-detail-content">
              {selectedProduct.image_url && (
                <div className="product-detail-image">
                  <img src={selectedProduct.image_url} alt={selectedProduct.product_name} />
                </div>
              )}

              <div className="product-detail-info">
                <h2>{selectedProduct.product_name}</h2>

                {selectedProduct.short_desc && (
                  <div className="detail-field">
                    <label>簡介：</label>
                    <p>{selectedProduct.short_desc}</p>
                  </div>
                )}

                {selectedProduct.product_family && (
                  <div className="detail-field">
                    <label>產品系列：</label>
                    <p>{selectedProduct.product_family}</p>
                  </div>
                )}

                {selectedProduct.sku && (
                  <div className="detail-field">
                    <label>SKU：</label>
                    <p>{selectedProduct.sku}</p>
                  </div>
                )}

                {selectedProduct.revision && (
                  <div className="detail-field">
                    <label>版本：</label>
                    <p>{selectedProduct.revision}</p>
                  </div>
                )}

                {selectedProduct.spec && (
                  <div className="detail-field">
                    <label>規格：</label>
                    <p>{selectedProduct.spec}</p>
                  </div>
                )}

                {selectedProduct.buy_spec && (
                  <div className="detail-field">
                    <label>購買規格：</label>
                    <p>{selectedProduct.buy_spec}</p>
                  </div>
                )}

                {selectedProduct.price && (
                  <div className="detail-field">
                    <label>價格：</label>
                    <p className="product-price">{selectedProduct.price}</p>
                  </div>
                )}

                {selectedProduct.status && (
                  <div className="detail-field">
                    <label>供應狀態：</label>
                    <p className={`product-status status-${selectedProduct.status}`}>
                      {statusConfig[selectedProduct.status as keyof typeof statusConfig]?.label ||
                        selectedProduct.status}
                    </p>
                  </div>
                )}

                {selectedProduct.buy_link && (
                  <div className="detail-field">
                    <a href={selectedProduct.buy_link} target="_blank" rel="noopener noreferrer" className="buy-link-btn">
                      前往購買
                    </a>
                  </div>
                )}

                {isAdmin && (
                  <div className="admin-actions">
                    <button className="edit-btn" onClick={() => openEditModal(selectedProduct)}>
                      <FaEdit /> 編輯
                    </button>
                    <button className="delete-btn" onClick={() => handleDeleteProduct(selectedProduct.id)}>
                      <FaTrash /> 刪除
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content product-form-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>
              <FaTimes />
            </button>

            <h2>新增產品</h2>
            <form onSubmit={handleAddProduct} className="product-form">
              <div className="form-group">
                <label>產品名稱 *</label>
                <input
                  type="text"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  required
                  placeholder="請輸入產品名稱"
                />
              </div>

              <div className="form-group">
                <label>簡介</label>
                <textarea
                  name="short_desc"
                  value={formData.short_desc}
                  onChange={handleInputChange}
                  placeholder="請輸入產品簡介"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>產品系列</label>
                  <input
                    type="text"
                    name="product_family"
                    value={formData.product_family}
                    onChange={handleInputChange}
                    placeholder="例：感測器"
                  />
                </div>

                <div className="form-group">
                  <label>SKU（型號）</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="例：SNS-XYZ-001"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>版本</label>
                  <input
                    type="text"
                    name="revision"
                    value={formData.revision}
                    onChange={handleInputChange}
                    placeholder="例：v2.1"
                  />
                </div>

                <div className="form-group">
                  <label>價格</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="例：$299.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>規格（尺寸）</label>
                <textarea
                  name="spec"
                  value={formData.spec}
                  onChange={handleInputChange}
                  placeholder="請輸入產品規格"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>購買規格（必要規格）</label>
                <textarea
                  name="buy_spec"
                  value={formData.buy_spec}
                  onChange={handleInputChange}
                  placeholder="例：最低訂購量：10 個"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>購買連結</label>
                <input
                  type="url"
                  name="buy_link"
                  value={formData.buy_link}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label>狀態</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="active">現有產品</option>
                  <option value="discontinued">已停產</option>
                  <option value="coming_soon">即將推出</option>
                  {isAdmin && <option value="hide">隱藏產品</option>}
                </select>
              </div>

              <div className="form-group">
                <label>產品圖片</label>
                <div className="image-upload-area">
                  {formData.image_url && (
                    <div className="image-preview">
                      <img src={formData.image_url} alt="Preview" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                    id="product-image-upload"
                  />
                  <label htmlFor="product-image-upload" className="upload-label">
                    {isUploading ? '上傳中...' : '選擇圖片'}
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddModal(false)}>
                  取消
                </button>
                <button type="submit" className="submit-btn" disabled={isUploading}>
                  新增產品
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content product-form-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeEditModal}>
              <FaTimes />
            </button>

            <h2>編輯產品</h2>
            <form onSubmit={handleEditProduct} className="product-form">
              <div className="form-group">
                <label>產品名稱 *</label>
                <input
                  type="text"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  required
                  placeholder="請輸入產品名稱"
                />
              </div>

              <div className="form-group">
                <label>簡介</label>
                <textarea
                  name="short_desc"
                  value={formData.short_desc}
                  onChange={handleInputChange}
                  placeholder="請輸入產品簡介"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>產品系列</label>
                  <input
                    type="text"
                    name="product_family"
                    value={formData.product_family}
                    onChange={handleInputChange}
                    placeholder="例：感測器"
                  />
                </div>

                <div className="form-group">
                  <label>SKU（型號）</label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    placeholder="例：SNS-XYZ-001"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>版本</label>
                  <input
                    type="text"
                    name="revision"
                    value={formData.revision}
                    onChange={handleInputChange}
                    placeholder="例：v2.1"
                  />
                </div>

                <div className="form-group">
                  <label>價格</label>
                  <input
                    type="text"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="例：$299.00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>規格（尺寸）</label>
                <textarea
                  name="spec"
                  value={formData.spec}
                  onChange={handleInputChange}
                  placeholder="請輸入產品規格"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>購買規格（必要規格）</label>
                <textarea
                  name="buy_spec"
                  value={formData.buy_spec}
                  onChange={handleInputChange}
                  placeholder="例：最低訂購量：10 個"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>購買連結</label>
                <input
                  type="url"
                  name="buy_link"
                  value={formData.buy_link}
                  onChange={handleInputChange}
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label>狀態</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="active">現有產品</option>
                  <option value="discontinued">已停產</option>
                  <option value="coming_soon">即將推出</option>
                  {isAdmin && <option value="hide">隱藏產品</option>}
                </select>
              </div>

              <div className="form-group">
                <label>產品圖片</label>
                <div className="image-upload-area">
                  {formData.image_url && (
                    <div className="image-preview">
                      <img src={formData.image_url} alt="Preview" />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                    id="product-image-upload-edit"
                  />
                  <label htmlFor="product-image-upload-edit" className="upload-label">
                    {isUploading ? '上傳中...' : '選擇圖片'}
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeEditModal}>
                  取消
                </button>
                <button type="submit" className="submit-btn" disabled={isUploading}>
                  更新產品
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
